/**
 * Prins: Components / Multi select
 * (Dependency: https://github.com/fabianlindfors/multi.js/)
 *
 * @copyright 2023 i-fabrik GmbH
 * @author Heiko Pfefferkorn
 */

import {normalizeAttributeValue} from '@ifabnpm/bootstrap/utils.js';
import {load} from '@ifabnpm/bootstrap/utils/load.js';

import SelectorEngine from '@ifabnpm/bootstrap/dom/selector-engine.js';
import EventHandler from '@ifabnpm/bootstrap/dom/event-handler.js';
import Data from '@ifabnpm/bootstrap/dom/data.js';
import Manipulator from '@ifabnpm/bootstrap/dom/manipulator.js';

import ClearableField from '@ifabnpm/bootstrap/component/form/clearable-field.js';
import Spinner from '@ifabnpm/bootstrap/component/spinner.js';

// -------
// Private
// -------

const NAME      = 'multi-select';
const DATA_KEY  = `ifab.${NAME}`;
const EVENT_KEY = `.${DATA_KEY}`;
// const API_KEY   = '.data-api';

const DEFAULTS = {
	infoSelected     : '({{selected}} / {{from}})',
	infoSelectedLimit: '({{selected}} / max. {{from}})'
};

const observerInView = new IntersectionObserver((entries) => {
	for (const element of entries) {
		if (element.isIntersecting) {
			render(element.target);
		}
	}
}, {
	root     : null,
	threshold: [0.25]
});

const handleSelection = (container, flag) => {
	const select = Data.get(container, `${DATA_KEY}.select`);
	const options = select.getElementsByTagName('option');

	for (const option of options) {
		option.selected = flag;
	}

	EventHandler.trigger(select, 'change');
};

const extendSearch = (select) => {
	const container = Data.get(select, `${DATA_KEY}.container`);
	const nonSelectedWrapper = (container) ? SelectorEngine.findOne('.non-selected-wrapper', container) : null;
	const selectedWrapper = (container) ? SelectorEngine.findOne('.selected-wrapper', container) : null;
	const searchInput = (container) ? SelectorEngine.findOne('.search-input', container) : null;

	if (searchInput && selectedWrapper) {
		EventHandler.on(selectedWrapper, `filterItems.${EVENT_KEY}`, (event) => {
			const query = searchInput.value;
			const selectedOptions = SelectorEngine.find('.item.selected', selectedWrapper);

			for (const item of selectedOptions) {
				if (query) {
					// Gibt es einen Suchbegriff...

					if (
						item.dataset.value.toLowerCase().indexOf(query.toLowerCase()) > -1 ||
						item.textContent.toLowerCase().indexOf(query.toLowerCase()) > -1
					) {
						delete item.hidden;
					} else {
						item.hidden = true;
					}
				} else {
					// Gibt es keinen Suchbegriff...

					delete item.hidden;
				}
			}
		});

		EventHandler.on(searchInput, 'input', (event) => {
			EventHandler.trigger(selectedWrapper, `filterItems.${EVENT_KEY}`);
		});

		const nonSelectedObserver = new MutationObserver((mutations) => {
			EventHandler.trigger(selectedWrapper, `filterItems.${EVENT_KEY}`);
		});

		nonSelectedObserver.observe(nonSelectedWrapper, {
			childList: true,
			attributes: true
		});

		new ClearableField(searchInput, {
			onClear: () => {
				EventHandler.trigger(searchInput, 'input');
				// searchInput.dispatchEvent(new Event('input', {bubbles: true}));
				// searchInput.dispatchEvent(new Event('change', {bubbles: true}));
			}
		});
	}
};

/**
 * ´Multi select´-Container initialisieren.
 *
 * @param {HTMLElement} container
 * @returns {HTMLElement}
 */
const render = async (container) => {
	const select           = Data.get(container, `${DATA_KEY}.select`);
	const info             = Data.get(container, `${DATA_KEY}.info`);
	const selectionButtons = SelectorEngine.find('button[data-select-all], button[data-remove-all]', container);

	//
	// Optionen zusammenstellen
	//

	const searchable        = container.dataset.searchable !== undefined;
	const searchPlaceholder = container.dataset.placeholder === undefined ? '' : container.dataset.placeholder;

	const params = {
		'enable_search'      : searchable,
		'search_placeholder' : searchPlaceholder//,
		// 'non_selected_header': (window.Prins.tm) ? window.Prins.tm.translate('selection') : '',
		// 'selected_header'    : (window.Prins.tm) ? window.Prins.tm.translate('selected') : ''
	};

	if (container.dataset.limit !== undefined) {
		params.limit = normalizeAttributeValue(container.dataset.limit);

		// for (const selBtn of selectionButtons) {
		// 	Manipulator.setDisabled(selBtn, true);
		// }
	}

	//
	// Information bzgl. ´ausgewählt´ integrieren.
	//

	if (info) {
		const infotext  = (params.limit) ? DEFAULTS.infoSelectedLimit : DEFAULTS.infoSelected;
		const countFrom = (params.limit) ?? select.options.length;

		EventHandler.on(select, `updateCount${EVENT_KEY}`, () => {
			const countCurrent = select.selectedOptions.length.toString();

			let s = infotext;

			s = s.replace(new RegExp('{{selected}}', 'gm'), countCurrent);
			s = s.replace(new RegExp('{{from}}', 'gm'), countFrom);

			info.textContent = s;

			if (params.limit !== undefined && params.limit === parseInt(countCurrent, 10)) {
				Manipulator.addClass(container, 'limit-reached');
			} else {
				Manipulator.removeClass(container, 'limit-reached');
			}
		});

		EventHandler.on(select, 'change', () => {
			const form = Data.get(select, `${DATA_KEY}.form`);

			EventHandler.trigger(select, `updateCount${EVENT_KEY}`);

			if (form) {
				EventHandler.trigger(form, 'changeDetected', {
					changeDetectedOn : select
				});
			}
		});

		EventHandler.trigger(select, `updateCount${EVENT_KEY}`);
	}

	// Plugin initialisieren.
	await multi(select, params);
	await extendSearch(select);

	EventHandler.on(container, `click${EVENT_KEY}`, 'button[data-select-all], button[data-remove-all]', (event) => {
		event.preventDefault();
		event.stopPropagation();

		handleSelection(container, event.delegateTarget.dataset.selectAll !== undefined);
	});

	// Spinner entfernen.
	Spinner.removeFrom(container);

	Manipulator.addClass(container, '-initialized');

	Data.set(container, `${DATA_KEY}.initialized`, 'true');
};

const preRender = (container) => {
	const form = SelectorEngine.parents(container, 'form')[0] ?? null;
	const select = SelectorEngine.findOne('select', container) ?? null;
	const selectIsMultiple = (select) ? select.multiple : false;
	const info = SelectorEngine.findOne('.multi-select-info', container) ?? null;

	if (Data.get(container, `${DATA_KEY}.initialized`)) {
		return container;
	}

	// Kein Formular, oder kein Select oder kein ...
	if (
		!form ||
		!select ||
		!selectIsMultiple
	) {
		if (select) {
			Manipulator.elementBefore(select, container);

			container.remove();
		}

		return container;
	}

	EventHandler.on(select, 'validationFailed.ifab', () => {
		Manipulator.addClass(container, '-validation-failed');
	});

	EventHandler.on(select, 'validationSuccess.ifab', () => {
		Manipulator.removeClass(container, '-validation-failed');
	});

	// Gegenseitige Elementreferenzen speichern.
	Data.set(container, `${DATA_KEY}.select`, select);
	Data.set(container, `${DATA_KEY}.form`, form);
	Data.set(container, `${DATA_KEY}.info`, info);
	Data.set(select, `${DATA_KEY}.container`, container);
	Data.set(select, `${DATA_KEY}.form`, form);
	Data.set(select, `${DATA_KEY}.info`, info);

	// Spinner dem Container hinzufügen
	Spinner.addTo(container);

	// Container überwachen.
	observerInView.observe(container);
};

// ------
// Public
// ------

/**
 * ´Sort-, Filterlist´-Elemente zusammenstellen und initialisieren.
 *
 * @returns {Array}
 */
const init = () => {
	let group = [];

	const collection = SelectorEngine.find(`[data-${NAME}]`);

	if (collection.length) {
		load('build/prins/components/custom/multi-select/bundle.js').then(() => {
			if (window.multi === undefined) {
				if (window.Prins.error) {
					window.Prins.error.show({
						msg: "The dependency ´multi.js´ cannot be loaded/initialized"
					});
				} else {
					throw new Error('The dependency ´multi.js´ cannot be loaded/initialized');
				}
			} else {
				for (const element of collection) {
					group.push(preRender(element));
				}
			}
		});
	}

	return group;
};

// Export
export default {
	init: init
};
